/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import * as drive from "../../../../api/drive";
import { useAuth } from "../../../../context/AuthContext";

// This theme uses `primary` as the page background, so MUI's default button
// colour is invisible. Dialog actions opt into explicit tokens instead.
const TEXT_BUTTON_SX = { color: "text.color", textTransform: "none" };
const ACCENT_BUTTON_SX = { backgroundColor: "accent.solid", color: "#fff", textTransform: "none" };
const TAB_SX = {
  textTransform: "none",
  minHeight: 40,
  fontSize: 13.5,
  "&.Mui-selected": { color: "accent.main" },
};

/** Create-folder and rename share one text-prompt shape. */
export const NamePromptDialog = ({ open, title, label, initialValue = "", confirmLabel, onConfirm, onClose }) => {
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValue(initialValue);
      setError(null);
    }
  }, [open, initialValue]);

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <form onSubmit={submit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            autoFocus
            fullWidth
            label={label}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy} sx={TEXT_BUTTON_SX}>Cancel</Button>
          <Button type="submit" variant="contained" sx={ACCENT_BUTTON_SX} disabled={busy || !value.trim()}>
            {confirmLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

/**
 * Destination picker for moving items. "My Drive" (null) is a real destination,
 * not an absent one — the API distinguishes an explicit null folder_id from an
 * omitted field, which is what makes moving back to root possible.
 */
export const MoveDialog = ({ open, items = [], onConfirm, onClose }) => {
  const [folders, setFolders] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setError(null);
    setFolders(null);
    drive
      .listFolderTree()
      .then(setFolders)
      .catch(() => setFolders([]));
  }, [open]);

  // Moving a folder into itself or its own subtree would orphan the branch.
  const movingFolderIds = new Set(items.filter((i) => i.type === "folder").map((i) => i.id));
  const selectable = (folders || []).filter((f) => !movingFolderIds.has(f.id));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(target);
      onClose();
    } catch (err) {
      setError(err.message || "Move failed.");
      setBusy(false);
    }
  };

  const label =
    items.length === 1 ? `“${items[0]?.name}”` : `${items.length} items`;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Move {label}</DialogTitle>
      <DialogContent sx={{ maxHeight: 360 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {folders === null ? (
          <DialogContentText sx={{ fontSize: 14 }}>Loading folders…</DialogContentText>
        ) : (
          <List dense disablePadding>
            <ListItemButton selected={target === null} onClick={() => setTarget(null)}>
              <ListItemText primary="My Drive" primaryTypographyProps={{ fontSize: 13.5 }} />
            </ListItemButton>
            {selectable.map((f) => (
              <ListItemButton
                key={f.id}
                selected={target === f.id}
                onClick={() => setTarget(f.id)}
                sx={{ pl: 2 + f.depth * 2 }}
              >
                <ListItemText
                  primary={f.folder_name}
                  primaryTypographyProps={{ fontSize: 13.5 }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={TEXT_BUTTON_SX}>Cancel</Button>
        <Button variant="contained" sx={ACCENT_BUTTON_SX} onClick={submit} disabled={busy}>
          Move here
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ConfirmDeleteDialog = ({ open, item, items, onConfirm, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setError(err.message || "Delete failed.");
      setBusy(false);
    }
  };

  // Single or bulk: `items` wins when present, so one component covers both.
  const list = items?.length ? items : item ? [item] : [];
  const many = list.length > 1;
  const hasFolder = list.some((i) => i.type === "folder");

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {many ? `Delete ${list.length} items?` : `Delete ${list[0]?.type}?`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText>
          {many ? (
            <>
              <strong>{list.length} items</strong> will be deleted.
            </>
          ) : (
            <>
              <strong>{list[0]?.name}</strong> will be deleted.
            </>
          )}
          {hasFolder && " Folder contents will no longer be reachable."}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} sx={TEXT_BUTTON_SX}>Cancel</Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={busy}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Three mutually-exclusive share targets, matching the API contract: a public
 * link, one person, or the caller's organisation. Public links are read-only —
 * the API rejects `permission: "write"` alongside `is_public` — so the
 * permission control is hidden on that tab rather than sending a doomed request.
 */
export const ShareDialog = ({ open, item, onClose }) => {
  // The org comes from AuthContext, which already loaded it. This used to
  // fetch a page of shares purely to read `organisation_id` off the envelope.
  const { activeOrgId: orgId } = useAuth();
  const [tab, setTab] = useState("link");
  const [permission, setPermission] = useState("read");
  const [email, setEmail] = useState("");

  const [link, setLink] = useState(null);
  const [expiryDays, setExpiryDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!open) return;
    setTab("link");
    setPermission("read");
    setEmail("");
    setLink(null);
    setExpiryDays(7);
    setError(null);
    setNotice(null);
  }, [open]);

  const run = async (fn, onDone) => {
    setBusy(true);
    setError(null);
    try {
      onDone(await fn());
    } catch (err) {
      setError(err.message || "Could not create the share.");
    } finally {
      setBusy(false);
    }
  };

  const createLink = () =>
    run(
      () =>
        drive.createShare({
          resourceType: item.type,
          resourceId: item.id,
          isPublic: true,
          // Null = never expires. Anything else is an absolute instant, so the
          // link's lifetime doesn't shift with the recipient's clock.
          expiresAt: expiryDays
            ? new Date(Date.now() + expiryDays * 86400000).toISOString()
            : null,
        }),
      ({ share }) =>
        // Our own /s/:token view, not the API's raw JSON endpoint, so a
        // recipient opening the link gets a page rather than a payload.
        setLink(`${window.location.origin}/s/${share.share_token}`)
    );

  const sharePerson = () =>
    run(
      async () => {
        const user = await drive.findUser(email.trim());
        const id = user?.id || user?.sub;
        if (!id) throw new Error("No account found for that email.");
        return drive.createShare({
          resourceType: item.type,
          resourceId: item.id,
          sharedWithUserId: id,
          permission,
        });
      },
      () => setNotice(`Shared with ${email.trim()} (${permission}).`)
    );

  const shareOrg = () =>
    run(
      () =>
        drive.createShare({
          resourceType: item.type,
          resourceId: item.id,
          sharedWithOrgId: orgId,
          permission,
        }),
      () => setNotice(`Shared with everyone in your organisation (${permission}).`)
    );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setNotice("Link copied.");
    } catch {
      setError("Copy failed — select the link and copy it manually.");
    }
  };

  const confirm = { link: createLink, person: sharePerson, organisation: shareOrg }[tab];
  const canConfirm =
    (tab === "link" && !link) ||
    (tab === "person" && email.trim() !== "") ||
    (tab === "organisation" && Boolean(orgId));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>Share “{item?.name}”</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          setError(null);
          setNotice(null);
        }}
        sx={{ px: 3, minHeight: 40, "& .MuiTabs-indicator": { backgroundColor: "accent.main" } }}
      >
        <Tab value="link" label="Public link" sx={TAB_SX} />
        <Tab value="person" label="Person" sx={TAB_SX} />
        <Tab
          value="organisation"
          label="Organisation"
          disabled={orgId === null}
          sx={TAB_SX}
        />
      </Tabs>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

        {tab === "link" &&
          (link ? (
            <TextField
              fullWidth
              value={link}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={copy} aria-label="Copy link"><ContentCopyIcon /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <>
              <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
                Creates a read-only link. Anyone with the link can view this {item?.type}.
              </DialogContentText>
              <TextField
                select
                fullWidth
                size="small"
                label="Link expires"
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
                disabled={busy}
              >
                <MenuItem value={1}>In 1 day</MenuItem>
                <MenuItem value={7}>In 7 days</MenuItem>
                <MenuItem value={30}>In 30 days</MenuItem>
                <MenuItem value={0}>Never</MenuItem>
              </TextField>
            </>
          ))}

        {tab === "person" && (
          <TextField
            autoFocus
            fullWidth
            label="Email address"
            placeholder="colleague@vecros.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        )}

        {tab === "organisation" && (
          <DialogContentText sx={{ fontSize: 14 }}>
            {orgId
              ? `Everyone in your organisation will be able to access this ${item?.type}.`
              : "Your account isn't part of an organisation."}
          </DialogContentText>
        )}

        {/* Public links cannot carry write access, so this only applies to the
            person and organisation targets. */}
        {tab !== "link" && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={permission}
            onChange={(_, v) => v && setPermission(v)}
            sx={{ mt: 2 }}
          >
            <ToggleButton value="read" sx={{ textTransform: "none" }}>Can view</ToggleButton>
            <ToggleButton value="write" sx={{ textTransform: "none" }}>Can edit</ToggleButton>
          </ToggleButtonGroup>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={TEXT_BUTTON_SX}>Close</Button>
        {canConfirm && (
          <Button variant="contained" sx={ACCENT_BUTTON_SX} onClick={confirm} disabled={busy}>
            {tab === "link" ? "Create link" : "Share"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
